"use client"

import type React from "react"
import { useState, useEffect, useRef } from "react"
import { Users, Building, BarChart3, User, Maximize2, X, ZoomIn, ZoomOut, Minimize2 } from "lucide-react"
import TabsContainer from "components/TabsContainer";
import Tab from "components/Tab";
import { getOrganizationOverview, getOrganizationTree } from "api/apiHelper"
import { usePermissions } from "app/hooks"
import { useNavigate } from "react-router-dom"
import config from "config";
import { useAppSelector } from "app/hooks";

interface Level {
  id: number
  code: string
  order: number
  promotion_timeline?: number
}

interface Department {
  id: number
  name: string
}

interface Title {
  id: number
  name: string
  level: Level
  department: Department
}

interface Employee {
  id: number
  first_name: string
  last_name: string
  title?: Title
  photo?: string
  is_active: boolean
  subordinates: Employee[]
}

interface OrganizationOverview {
  levels: Level[]
  departments: Department[]
  titles: Title[]
  employees: Employee[]
}

const TreeNode: React.FC<{
  employee: Employee
  level: number
  onNodeClick: (employee: Employee) => void
  currentUserId?: number
}> = ({ employee, level, onNodeClick, currentUserId }) => {
  const [showDetails, setShowDetails] = useState(false)
  const [lineStyle, setLineStyle] = useState<{ left: string; width: string } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const hasSubordinates = employee.subordinates && employee.subordinates.length > 0

  // Calculate line positioning after render
  useEffect(() => {
    console.log("Level: ", level);
    if (employee.subordinates.length > 1 && containerRef.current) {
      const container = containerRef.current
      const classNames = `.child-node .level-${level + 1}`
      const children = container.querySelectorAll(classNames)
      
      
      if (children.length > 1) {
        const firstChild = children[0] as HTMLElement
        const lastChild = children[children.length - 1] as HTMLElement        
        const containerRect = container.getBoundingClientRect()
        const firstChildRect = firstChild.getBoundingClientRect()
        const lastChildRect = lastChild.getBoundingClientRect()
        
        const firstChildCenter = firstChildRect.left + firstChildRect.width / 2 - containerRect.left
        const lastChildCenter = lastChildRect.left + lastChildRect.width / 2 - containerRect.left
        
        const lineLeft = firstChildCenter
        const lineWidth = lastChildCenter - firstChildCenter
        
        setLineStyle({
          left: `${lineLeft}px`,
          width: `${lineWidth}px`
        })
      }
    }
  }, [employee.subordinates.length])

  const getLevelColor = (levelCode?: string, isCurrentUser?: boolean) => {
    if (isCurrentUser) {
      return "ring-yellow-500 shadow-yellow-300 bg-gradient-to-br from-yellow-50 to-yellow-100 ring-4 animate-pulse"
    }
    const colors: Record<string, string> = {
      L1: "ring-red-500 shadow-red-300 bg-gradient-to-br from-red-50 to-red-100", // CEO
      L2: "ring-orange-500 shadow-orange-300 bg-gradient-to-br from-orange-50 to-orange-100", // VP/Director
      L3: "ring-blue-500 shadow-blue-300 bg-gradient-to-br from-blue-50 to-blue-100", // Manager
      L4: "ring-green-500 shadow-green-300 bg-gradient-to-br from-green-50 to-green-100", // Senior
      L5: "ring-purple-500 shadow-purple-300 bg-gradient-to-br from-purple-50 to-purple-100", // Trainee
    }
    return colors[levelCode || ""] || "ring-gray-500 shadow-gray-300 bg-gradient-to-br from-gray-50 to-gray-100"  
  }

  const nodeSize = level === 0 ? "w-20 h-20" : level === 1 ? "w-16 h-16" : "w-14 h-14"
  const ringSize = "ring-4"
  const isCurrentUser = employee.id === currentUserId

  return (
    <div className="flex flex-col items-center">
      {/* Employee Node */}
      <div className={`relative ${showDetails ? "z-[9998]" : "z-20"} level-${level}`}>
        <div
          className={`${getLevelColor(employee.title?.level?.code, isCurrentUser)} ${nodeSize} ${ringSize} rounded-full cursor-pointer transition-all duration-300 hover:scale-110 hover:shadow-2xl bg-white overflow-hidden`}          
          onClick={() => {
            setShowDetails(!showDetails)
            onNodeClick(employee)
          }}
        >
          {employee.photo ? (
            <img
              src={employee.photo || "/placeholder.svg"}
              alt={`${employee.first_name} ${employee.last_name}`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold text-lg">
              {employee.first_name.charAt(0)}
              {employee.last_name.charAt(0)}
            </div>
          )}
        </div>

        <div
          className={`absolute -bottom-1 -right-1 px-2 py-1 rounded-full text-xs font-bold text-white shadow-lg ${employee.title?.level?.code === "L1" ? "bg-red-500" : employee.title?.level?.code === "L2" ? "bg-orange-500" : employee.title?.level?.code === "L3" ? "bg-blue-500" : employee.title?.level?.code === "L4" ? "bg-green-500" : "bg-purple-500"}`}
        >
          {employee.title?.level?.code || "N/A"}
        </div>

        {isCurrentUser && (
          <div className="absolute -top-2 -left-2 bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg border-2 border-white">
            You
          </div>
        )}

        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-4">
          <div className="text-sm font-semibold text-navy-700 text-center max-w-[150px] truncate mx-auto">
            {employee.first_name} {employee.last_name}
          </div>
        </div>

        {/* Employee Details Card */}
        {showDetails && (
          <div 
            className="absolute left-full top-1/2 transform -translate-y-1/2 ml-4
                      bg-white rounded-lg shadow-xl border border-gray-200 z-[9999] w-64"
          >
            <div className="p-4 relative">
              {/* Close button */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setShowDetails(false)
                }}
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full w-6 h-6 flex items-center justify-center transition-colors text-xs"
              >
                ✕
              </button>

              {/* Profile section */}
              <div className="text-center mb-4">
                <div
                  className={`w-16 h-16 mx-auto mb-3 rounded-full overflow-hidden ring-4 ${getLevelColor(employee.title?.level?.code, isCurrentUser)} shadow-lg`}
                >
                  {employee.photo ? (
                    <img
                      src={employee.photo || "/placeholder.svg"}
                      alt={`${employee.first_name} ${employee.last_name}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                      {employee.first_name.charAt(0)}
                      {employee.last_name.charAt(0)}
                    </div>
                  )}
                </div>
                <h3 className="font-bold text-sm text-gray-900 mb-1">
                  {employee.first_name} {employee.last_name}
                  {isCurrentUser && <span className="ml-2 text-yellow-600 text-sm">(You)</span>}
                </h3>
              </div>

              {/* Details grid */}
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-md p-3">
                  <div className="text-xs text-gray-600 mb-1">Level</div>
                  <div
                    className={`inline-flex px-2 py-1 rounded-full text-xs font-bold text-white ${
                      employee.title?.level?.code === "L1"
                        ? "bg-red-500"
                        : employee.title?.level?.code === "L2"
                          ? "bg-orange-500"
                          : employee.title?.level?.code === "L3"
                            ? "bg-blue-500"
                            : employee.title?.level?.code === "L4"
                              ? "bg-green-500"
                              : "bg-purple-500"
                    }`}
                  >
                    {employee.title?.level?.code || "N/A"}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-md p-3">
                  <div className="text-xs text-gray-600 mb-1">Department</div>
                  <div className="font-medium text-xs text-gray-900">{employee.title?.department?.name || "No Department"}</div>
                </div>

                <div className="bg-gray-50 rounded-md p-3">
                  <div className="text-xs text-gray-600 mb-1">Title</div>
                  <div className="font-medium text-xs text-gray-900">{employee.title?.name || "No Title"}</div>
                </div>

                {hasSubordinates && (
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-md p-3">
                    <div className="text-xs text-gray-600 mb-1">Reports</div>
                    <div className="font-semibold text-xs text-blue-700">
                      {employee.subordinates.length} Direct Report{employee.subordinates.length !== 1 ? "s" : ""}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Subordinates with precisely calculated connecting lines */}
      {hasSubordinates && employee.subordinates.length > 0 && (
        <div className="relative mt-20" ref={containerRef}>
          {/* Vertical line from parent going downwards */}
          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 w-0.5 h-8 bg-gray-400 z-10"></div>

          {/* Container for children with adaptive spacing based on name lengths */}
          <div className="flex justify-center items-start gap-20 relative" style={{ paddingTop: "32px" }}>
            {employee.subordinates.map((subordinate, index) => (
              <div key={subordinate.id} className={`relative flex flex-col items-center child-node`}>
                {/* Vertical line going upwards to connect to horizontal line */}
                <div className="w-0.5 h-8 bg-gray-400 z-10" style={{ marginTop: "-32px", marginBottom: "24px" }}></div>

                <TreeNode
                  employee={subordinate}
                  level={level + 1}
                  onNodeClick={onNodeClick}
                  currentUserId={currentUserId}
                />
              </div>
            ))}
            
            {/* Horizontal connecting line with precisely calculated dimensions */}
            {employee.subordinates.length > 1 && lineStyle && (
              <div 
                className="absolute bg-gray-400 h-0.5 z-10"
                style={{
                  top: "0px",
                  left: lineStyle.left,
                  width: lineStyle.width,
                }}
              ></div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const StatisticsView: React.FC<{ overview: OrganizationOverview }> = ({ overview }) => {
  const stats = {
    totalEmployees: overview.employees.length,
    withTitles: overview.employees.filter((emp) => emp.title).length,
    withoutTitles: overview.employees.filter((emp) => !emp.title).length,
    totalDepartments: overview.departments.length,
    totalLevels: overview.levels.length,
  }
  
  const levelDistribution = overview.levels
  .map((level) => {
    const count = overview.employees.filter((emp) => emp.title?.level?.code === level.code).length
    return {
      level: level.code,
      count,
      percentage: overview.employees.length > 0 ? (count / overview.employees.length) * 100 : 0,
      color:
        level.code === "L1"
          ? "#ef4444"
          : level.code === "L2"
            ? "#f97316"
            : level.code === "L3"
              ? "#3b82f6"
              : level.code === "L4"
                ? "#22c55e"
                : "#a855f7",
    }
  })
  .sort((a, b) => a.level.localeCompare(b.level))

  // Calculate department distribution
  const departmentDistribution = overview.departments
    .map((dept) => {
      const count = overview.employees.filter((emp) => emp.title?.department?.id === dept.id).length
      return {
        name: dept.name,
        count,
        percentage: overview.employees.length > 0 ? (count / overview.employees.length) * 100 : 0,
      }
    })
    .sort((a, b) => b.count - a.count)

  // Calculate completion rate
  const completionRate = overview.employees.length > 0 ? (stats.withTitles / overview.employees.length) * 100 : 0

  const monthFormatter = new Intl.DateTimeFormat("en", { month: "short" })
  const months = Array.from({ length: 12 }, (_, i) => {
    const d = new Date()
    d.setMonth(d.getMonth() - (11 - i))
    return monthFormatter.format(d)
  })

  // Keep prior color map for levels
  const levelColorMap: Record<string, string> = {
    L1: "#ef4444",
    L2: "#f97316",
    L3: "#3b82f6",
    L4: "#22c55e",
    L5: "#a855f7",
  }

  // Determine which level codes to chart
  const levelCodes = levelDistribution.map((l) => l.level)

  // Flat baseline trend using current counts (replace with real historical data when available)
  const levelTrendData = months.map((m) => {
    const entry: Record<string, number | string> = { month: m }
    levelCodes.forEach((code) => {
      const ld = levelDistribution.find((x) => x.level === code)
      entry[code] = ld?.count ?? 0
    })
    return entry
  })

  // Limit departments to top 5 for readability
  const topDepartments = departmentDistribution.slice(0, 5).map((d) => d.name)
  const departmentTrendData = months.map((m) => {
    const entry: Record<string, number | string> = { month: m }
    topDepartments.forEach((name, index) => {
      const dd = departmentDistribution.find((x) => x.name === name)
      entry[name] = dd?.count ?? 0
    })
    return entry
  })

  return (
    <div className="space-y-8">
    {/* Overview Cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
      <div className="bg-blue-100 rounded-xl shadow-lg p-6 text-center">
        <Users className="w-10 h-10 text-blue-600 mx-auto mb-3" />
        <div className="text-3xl font-bold text-blue-900">{stats.totalEmployees}</div>
        <div className="text-sm text-blue-700 font-medium">Total Employees</div>
      </div>
      <div className="bg-blue-100 rounded-xl shadow-lg p-6 text-center">
        <User className="w-10 h-10 text-blue-600 mx-auto mb-3" />
        <div className="text-3xl font-bold text-blue-900">{stats.withTitles}</div>
        <div className="text-sm text-blue-700 font-medium">With Titles</div>
      </div>
      <div className="bg-blue-100 rounded-xl shadow-lg p-6 text-center">
        <User className="w-10 h-10 text-blue-600 mx-auto mb-3" />
        <div className="text-3xl font-bold text-blue-900">{stats.withoutTitles}</div>
        <div className="text-sm text-blue-700 font-medium">Without Titles</div>
      </div>
      <div className="bg-blue-100 rounded-xl shadow-lg p-6 text-center">
        <Building className="w-10 h-10 text-blue-600 mx-auto mb-3" />
        <div className="text-3xl font-bold text-blue-900">{stats.totalDepartments}</div>
        <div className="text-sm text-blue-700 font-medium">Departments</div>
      </div>
      <div className="bg-blue-100 rounded-xl shadow-lg p-6 text-center">
        <BarChart3 className="w-10 h-10 text-blue-600 mx-auto mb-3" />
        <div className="text-3xl font-bold text-blue-900">{stats.totalLevels}</div>
        <div className="text-sm text-blue-700 font-medium">Levels</div>
      </div>
    </div>

    {/* Completion Rate Progress */}
    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
      <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
        <BarChart3 className="w-6 h-6 text-blue-600 mr-2" />
        Organization Completion Rate
      </h3>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <span className="text-gray-700 font-medium">Employees with Assigned Titles</span>
          <span className="text-2xl font-bold text-blue-600">{completionRate.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-1000 ease-out"
            style={{ width: `${completionRate}%` }}
          ></div>
        </div>
        <div className="flex justify-between text-sm text-gray-600">
          <span>{stats.withTitles} assigned</span>
          <span>{stats.withoutTitles} pending</span>
        </div>
      </div>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Level Distribution Chart */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <Users className="w-6 h-6 text-purple-600 mr-2" />
          Level Distribution
        </h3>
        <div className="h-64 flex items-end justify-around gap-4 px-4">
          {levelDistribution.map((level) => (
            <div key={level.level} className="flex flex-col items-center flex-1 max-w-[80px]">
              {/* Count and percentage label */}
              <div className="text-center mb-2">
                <div className="font-bold text-gray-900 text-sm">{level.count}</div>
                <div className="text-xs text-gray-500">({level.percentage.toFixed(1)}%)</div>
              </div>

              {/* Vertical bar */}
              <div className="w-2/3 h-48 bg-gray-200 rounded-t-lg overflow-hidden flex flex-col justify-end">
                <div
                  className="w-full rounded-t-lg transition-all duration-1000 ease-out"
                  style={{
                    height: `${level.percentage}%`,
                    backgroundColor: level.color,
                  }}
                ></div>
              </div>

              {/* Level label at bottom */}
              <div className="mt-2 flex items-center justify-center">
                <div className="w-3 h-3 rounded-full mr-1" style={{ backgroundColor: level.color }}></div>
                <span className="font-medium text-gray-700 text-sm">Level {level.level}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Department Distribution */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <Building className="w-6 h-6 text-green-600 mr-2" />
          Department Distribution
        </h3>
        <div className="space-y-4">
          {departmentDistribution.slice(0, 6).map((dept, index) => (
            <div key={dept.name} className="space-y-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{
                      backgroundColor: `hsl(${(index * 60) % 360}, 70%, 50%)`,
                    }}
                  ></div>
                  <span className="font-medium text-gray-700 truncate max-w-32">{dept.name}</span>
                </div>
                <div className="text-right">
                  <span className="font-bold text-gray-900">{dept.count}</span>
                  <span className="text-sm text-gray-500 ml-1">({dept.percentage.toFixed(1)}%)</span>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-1000 ease-out"
                  style={{
                    width: `${dept.percentage}%`,
                    backgroundColor: `hsl(${(index * 60) % 360}, 70%, 50%)`,
                  }}
                ></div>
              </div>
            </div>
          ))}
          {departmentDistribution.length > 6 && (
            <div className="text-center text-sm text-gray-500 pt-2">
              +{departmentDistribution.length - 6} more departments
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Quick Insights */}
    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl shadow-lg p-6 border border-indigo-200">
      <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
        <BarChart3 className="w-6 h-6 text-indigo-600 mr-2" />
        Quick Insights
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-indigo-600">
            {levelDistribution.reduce((max, level) => (level.count > max.count ? level : max), levelDistribution[0])
              ?.level || "N/A"}
          </div>
          <div className="text-sm text-gray-600">Most Common Level</div>
        </div>
        <div className="bg-white rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">
            {departmentDistribution[0]?.name?.substring(0, 10) || "N/A"}
            {departmentDistribution[0]?.name?.length > 10 ? "..." : ""}
          </div>
          <div className="text-sm text-gray-600">Largest Department</div>
        </div>
        <div className="bg-white rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-600">
            {completionRate > 80 ? "Excellent" : completionRate > 60 ? "Good" : "Needs Work"}
          </div>
          <div className="text-sm text-gray-600">Organization Health</div>
        </div>
      </div>
    </div>
  </div>
)}

const FullscreenChartModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  orgData: Employee[]
  currentUserId?: number
  onNodeClick: (employee: Employee) => void
}> = ({ isOpen, onClose, orgData, currentUserId, onNodeClick }) => {
  const [zoom, setZoom] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "unset"
    }
    return () => {
      document.body.style.overflow = "unset"
    }
  }, [isOpen])

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.2, 3))
  }

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.2, 0.3))
  }

  const handleResetZoom = () => {
    setZoom(1)
    setPosition({ x: 0, y: 0 })
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0) {
      setIsDragging(true)
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y })
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault()
    const delta = e.deltaY * -0.001
    setZoom((prev) => Math.max(0.3, Math.min(3, prev + delta)))
  }

  const handleClose = () => {
    setZoom(1)
    setPosition({ x: 0, y: 0 })
    setIsDragging(false)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[9999] bg-white flex items-center justify-center">
      {/* Controls */}
      <div className="absolute top-4 right-4 flex items-center gap-2 z-[10000]">
        <div className="bg-white rounded-lg shadow-lg p-2 flex items-center gap-2 border border-gray-200">
          <button onClick={handleZoomOut} className="p-2 hover:bg-gray-100 rounded transition-colors" title="Zoom Out">
            <ZoomOut className="w-5 h-5 text-gray-700" />
          </button>
          <span className="text-sm font-medium text-gray-700 min-w-[60px] text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={handleZoomIn} className="p-2 hover:bg-gray-100 rounded transition-colors" title="Zoom In">
            <ZoomIn className="w-5 h-5 text-gray-700" />
          </button>
          <div className="w-px h-6 bg-gray-300"></div>
          <button
            onClick={handleResetZoom}
            className="p-2 hover:bg-gray-100 rounded transition-colors"
            title="Reset View"
          >
            <Minimize2 className="w-5 h-5 text-gray-700" />
          </button>
        </div>
        <button
          onClick={handleClose}
          className="bg-white p-2 rounded-lg shadow-lg hover:bg-gray-100 transition-colors border border-gray-200"
          title="Close Fullscreen"
        >
          <X className="w-5 h-5 text-gray-700" />
        </button>
      </div>

      {/* Instructions */}
      <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-3 text-sm text-gray-700 z-[10000] border border-gray-200">
        <div className="font-semibold mb-1">Navigation:</div>
        <div>• Drag to pan</div>
        <div>• Scroll to zoom</div>
        <div>• Click nodes for details</div>
      </div>

      {/* Chart Container */}
      <div
        ref={containerRef}
        className="w-full h-full overflow-hidden cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        <div
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
            transformOrigin: "center center",
            transition: isDragging ? "none" : "transform 0.1s ease-out",
          }}
          className="inline-block min-w-full min-h-full p-20"
        >
          <div className="flex justify-center items-start">
            {orgData.map((employee) => (
              <TreeNode
                key={employee.id}
                employee={employee}
                level={0}
                onNodeClick={onNodeClick}
                currentUserId={currentUserId}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

const OrganizationChart: React.FC = () => {
  const [orgData, setOrgData] = useState<Employee[]>([])
  const [overview, setOverview] = useState<OrganizationOverview | null>(null)
  const [activeTab, setActiveTab] = useState<"chart" | "stats">("chart")
  const [isFullscreen, setIsFullscreen] = useState(false)
  const permissions = usePermissions() || []
  const navigate = useNavigate()
  const { empID } = useAppSelector((state) => ({
    empID: state.emp.id,
  }))
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      const [overviewResponse, treeResponse] = await Promise.all([getOrganizationOverview(), getOrganizationTree()])

      // Transform overview data
      const transformedOverview: OrganizationOverview = {
        levels: overviewResponse.data?.levels || [],
        departments: overviewResponse.data?.departments || [],
        titles: overviewResponse.data?.titles || [],
        employees: overviewResponse.data?.employees || [],
      }

      // Transform tree data
      const transformEmployee = (emp: any): Employee => ({
        id: emp.id,
        first_name: emp.first_name,
        last_name: emp.last_name,
        title: emp.title,
        photo: emp.photo ? `${config.app.BACKEND_URL}/${emp.photo}` : null,
        is_active: emp.is_active !== false,
        subordinates: emp.subordinates?.map(transformEmployee) || [],
      })

      const transformedOrgData = treeResponse.data?.map(transformEmployee) || []

      setOverview(transformedOverview)
      setOrgData(transformedOrgData)
    } catch (err) {
      console.error("Error fetching organization data:", err)
      setError("Failed to load organization data")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleNodeClick = (employee: Employee) => {
    console.log("Selected employee:", employee)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading organization chart...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">Error</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={fetchData} className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600">
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-end">
          {permissions.includes("organization_structure.add_level") && (
            <button
              onClick={() => navigate("/organization-structure/levels")}
              className="flex items-center px-4 py-2 bg-brand-500 hover:bg-brand-600 dark:hover:bg-brand-700 dark:bg-brand-400 text-white text-md font-medium rounded-lg transition-colors duration-200"
            >
              <i className="fas fa-plus mr-2"></i>
              Add Level
            </button>
          )}
        </div>
        {/* Navigation Tabs */}
        <TabsContainer>
          <Tab isActive={activeTab === "chart"} title="Organization Chart" onClick={() => setActiveTab("chart")}>
            {/* Organization Tree */}
            <div className="bg-white rounded-lg shadow p-8">
              {/* Header with fullscreen button */}
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-xl font-semibold text-gray-900">Organization Tree</h2>
                {orgData && orgData.length > 0 && (
                  <button
                    onClick={() => setIsFullscreen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-brand-500 hover:bg-brand-600 dark:hover:bg-brand-700 dark:bg-brand-400 text-white font-medium rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
                  >
                    <Maximize2 className="w-4 h-4" />
                    View Full Organization Chart
                  </button>
                )}
              </div>

              {orgData && orgData.length > 0 ? (
                <div className="overflow-x-auto">
                  <div className="min-w-max py-8">
                    {orgData.map((employee) => (
                      <TreeNode
                        key={employee.id}
                        employee={employee}
                        level={0}
                        onNodeClick={handleNodeClick}
                        currentUserId={empID}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium mb-2">No employees found</h3>
                  <p>No organizational data available</p>
                </div>
              )}
            </div>
          </Tab>
          {permissions.includes("employees.view_employee") && (
            <Tab isActive={activeTab === "stats"} title="Statistics" onClick={() => setActiveTab("stats")}>
              {overview && <StatisticsView overview={overview} />}
            </Tab>
          )}
        </TabsContainer>
      </div>

      {/* Fullscreen modal */}
      <FullscreenChartModal
        isOpen={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        orgData={orgData}
        currentUserId={empID}
        onNodeClick={handleNodeClick}
      />
    </div>
  )
}

export default OrganizationChart